import React, { useRef, useEffect, useState } from 'react';
import { ACHIEVEMENTS } from '../data/achievements_db';
import SceneCanvas from './SceneCanvas';

// --- COMMANDER'S LOG ---
const ActionLog = ({ logs }) => {
    const scrollRef = useRef();
    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = 0; }, [logs]);
    return (
        <div className="flex flex-col h-full bg-[#1c1917] border border-[#f59e0b]/20 rounded-xl p-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-[#f59e0b]/5 to-transparent pointer-events-none"></div>
            <div className="text-[10px] font-black text-[#f59e0b]/40 mb-4 border-b border-[#44403c] pb-2 tracking-[0.3em] uppercase">Commander's Log</div>
            <div ref={scrollRef} className="flex-grow overflow-y-auto space-y-2 font-mono text-[11px] pr-2 z-10">
                {logs.map((log, i) => {
                    const isAlert   = log.startsWith('⚠');
                    const isConsumed = log.startsWith('  └');
                    const isAchieve = log.startsWith('✦');
                    const isCrit    = log.includes('CRITICAL SUCCESS') || log.includes('CRITICAL FUMBLE');
                    return (
                        <div key={i} className={`leading-relaxed ${
                            isAlert   ? 'text-rose-400 font-bold not-italic' :
                            isAchieve ? 'text-sky-400 font-bold not-italic' :
                            isCrit    ? 'text-amber-300 font-bold italic' :
                            isConsumed ? 'text-slate-700 text-[10px]' :
                            i === 0   ? 'text-[#f59e0b] italic' :
                            'text-[#78716c] text-[10px] italic'
                        }`}>
                            {log}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};


// --- TACTICAL MAP ---
const TacticalMap = ({ grid, selectedTile, onSelect, position }) => (
    <div className="grid grid-cols-[repeat(15,minmax(0,1fr))] gap-[2px] bg-black/40 p-2 border border-[#f59e0b]/20 rounded-lg shadow-inner w-full max-w-[460px] mx-auto">
        {grid.flat().map((tile, i) => {
            const isPlayer = tile.x === position.x && tile.y === position.y;
            const isSelected = selectedTile?.x === tile.x && selectedTile?.y === tile.y;
            let bg = 'bg-black/20', border = 'border-white/5', text = 'text-white/10', label = '';
            if (isPlayer) { bg = 'bg-emerald-500/20'; border = 'border-emerald-500'; text = 'text-emerald-400'; label = '◈'; }
            else if (tile.scanned) {
                if (tile.type === 'HOSTILE') { border = 'border-rose-500/50'; text = 'text-rose-500'; label = '!!'; bg = 'bg-rose-500/5'; }
                else if (tile.type === 'RESOURCE') { border = 'border-sky-500/50'; text = 'text-sky-400'; label = '$$'; bg = 'bg-sky-500/5'; }
                else if (tile.type === 'WILDLIFE') { border = 'border-emerald-500/50'; text = 'text-emerald-400'; label = '🐾'; bg = 'bg-emerald-500/5'; }
                else { border = 'border-white/10'; text = 'text-white/30'; label = '··'; }
            }
            if (isSelected) { border = 'border-[#f59e0b]'; bg = 'bg-[#f59e0b]/10'; }
            return (
                <div
                    key={i}
                    onClick={() => onSelect(tile)}
                    className={`aspect-square border flex items-center justify-center text-[7px] mono cursor-crosshair transition-all ${bg} ${border} ${text} hover:bg-sky-500/20 hover:border-sky-500`}
                >
                    {label}
                </div>
            );
        })}
    </div>
);

const MapLegend = () => (
    <div className="flex flex-wrap justify-center gap-4 mt-2 p-2 bg-black/20 border border-white/5 rounded-lg mono text-[7px] uppercase tracking-widest text-[#f59e0b]/60">
        <div className="flex items-center gap-1"><span className="text-emerald-400">◈</span> You</div>
        <div className="flex items-center gap-1"><span className="text-rose-500">!!</span> Hostile Force</div>
        <div className="flex items-center gap-1"><span className="text-sky-400">$$</span> Resource Cache</div>
        <div className="flex items-center gap-1"><span className="text-emerald-400">🐾</span> Wildlife</div>
        <div className="flex items-center gap-1"><span className="text-white/30">··</span> Empty/Scanned</div>
    </div>
);

// --- EVENT OVERLAY ---
const EventOverlay = ({ event, gameState, dispatch }) => {
    if (!event) return null;
    const def = gameState.resources.defense;
    const shieldNote = event.id === 'hostile_contact'
        ? def >= 5 ? '✦ HIGH DEFENSE: Fortifications will repel this threat.'
        : def >= 2 ? '◈ PARTIAL DEFENSE: Some protection. Expect losses.'
        : '⚠ NO DEFENSE: Compound is vulnerable. Fortify after this.'
        : null;

    return (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="glass border-2 border-rose-500/60 rounded-2xl p-6 max-w-md w-full shadow-2xl shadow-rose-500/10 animate-fade-in overflow-y-auto max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse shrink-0"></div>
                    <span className="text-[11px] mono font-black text-rose-400 tracking-[0.25em] uppercase">{event.name}</span>
                </div>

                {/* Samantha Quote */}
                <div className="border-l-2 border-[#f59e0b]/40 pl-3 mb-4">
                    <p className="text-[#f59e0b]/70 mono text-[11px] italic leading-relaxed">
                        <span className="text-[#f59e0b]/40 font-bold not-italic">SAMANTHA: </span>
                        "{event.samanthaQuote}"
                    </p>
                </div>

                {/* Situation Description */}
                <p className="text-white text-sm leading-relaxed mb-4 font-medium">
                    {event.description}
                </p>

                {/* Defense hint for raids */}
                {shieldNote && (
                    <div className={`text-[10px] mono mb-4 px-3 py-2 rounded border ${
                        def >= 5 ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
                        def >= 2 ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' :
                        'bg-rose-500/10 border-rose-500/30 text-rose-400'
                    }`}>
                        {shieldNote}
                    </div>
                )}

                {/* Trait hints */}
                {(() => {
                    const traits = gameState.family.flatMap(m => m.traits);
                    const relevantTraits = {
                        hostile_contact: ['agile', 'strong'],
                        illness_outbreak: ['medic'],
                        water_contamination: ['engineer'],
                        supply_cache: ['hunter', 'agile'],
                        stranger_arrives: ['charismatic'],
                        equipment_failure: ['mechanic'],
                        dust_storm: ['agile'],
                        radio_signal: ['charismatic'],
                        morale_crisis: ['leader'],
                        trade_caravan: ['charismatic'],
                        wildlife: ['hunter'],
                        structural_damage: ['engineer', 'mechanic'],
                        ammo_barter: ['charismatic'],
                        tribute_demand: ['strong', 'charismatic'],
                        gun_runner: ['mechanic'],
                        spoiled_supplies: ['medic'],
                        fuel_leak: ['mechanic'],
                        cold_snap: ['strong'],
                        lost_scout: ['agile'],
                        scavenger_intel: ['hunter', 'agile'],
                    }[event.id] || [];
                    const activeTrait = relevantTraits.find(t => traits.includes(t));
                    if (!activeTrait) return null;
                    const traitLabels = { agile: 'AGILE', hunter: 'HUNTER', medic: 'MEDIC', engineer: 'ENGINEER', mechanic: 'MECHANIC', charismatic: 'CHARISMATIC', leader: 'LEADER', strong: 'STRONG' };
                    return (
                        <div className="text-[10px] mono mb-4 px-3 py-2 rounded bg-sky-500/10 border border-sky-500/30 text-sky-300">
                            ✦ TRAIT ADVANTAGE: {traitLabels[activeTrait]} detected in unit. Outcomes improved.
                        </div>
                    );
                })()}

                {/* Choice Buttons */}
                <div className="space-y-3 mt-2">
                    {event.choices.map(choice => (
                        <button
                            key={choice.id}
                            onClick={() => dispatch({ type: 'RESOLVE_EVENT', payload: { choiceId: choice.id } })}
                            className="w-full text-left bg-[#292524] border border-[#44403c] p-4 rounded-xl hover:border-[#f59e0b] hover:bg-[#f59e0b]/5 transition-all group"
                        >
                            <div className="text-white font-bold text-sm uppercase tracking-tight group-hover:text-[#f59e0b]">
                                {choice.label}
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- RESOURCE BAR ---
const ResourceBar = ({ label, val, max, color, warning }) => (
    <div className="w-full">
        <div className="flex justify-between text-[9px] mono font-bold mb-1 uppercase">
            <span className={warning && val < warning ? 'text-rose-400 animate-pulse' : 'opacity-50'}>{label}</span>
            <span className={warning && val < warning ? 'text-rose-400' : 'opacity-60'}>{Math.floor(val)}</span>
        </div>
        <div className="w-full h-1.5 bg-black/40 rounded-full overflow-hidden border border-white/5">
            <div className={`h-full bg-gradient-to-r ${color} transition-all duration-700`} style={{ width: `${Math.min(100, (val / max) * 100)}%` }}></div>
        </div>
    </div>
);

// --- ACTION BUTTON ---
const ActionButton = ({ label, sub, onClick, disabled, highlight }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        className={`w-full text-left border p-3 rounded-xl transition-all group ${
            disabled ? 'bg-black/20 border-white/5 opacity-40 cursor-not-allowed' :
            highlight ? 'bg-sky-500/10 border-sky-500/40 hover:border-sky-400 hover:bg-sky-500/20' :
            'bg-[#292524] border-[#44403c] hover:border-[#f59e0b]'
        }`}
    >
        <div className={`font-bold text-sm uppercase tracking-tight ${
            disabled ? 'text-white/40' :
            highlight ? 'text-sky-300 group-hover:text-sky-200' :
            'text-white group-hover:text-[#f59e0b]'
        }`}>{label}</div>
        <div className="text-[9px] mono text-[#a8a29e] mt-0.5">{sub}</div>
    </button>
);

// --- ACHIEVEMENT MODAL ---
const AchievementModal = ({ achievements, onClose }) => {
    const unlockedIds = achievements || [];
    return (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-300">
            <div className="glass border border-[#f59e0b]/30 rounded-3xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto relative shadow-[0_0_50px_rgba(245,158,11,0.1)]">
                <button onClick={onClose} className="absolute top-6 right-6 text-[#f59e0b]/40 hover:text-[#f59e0b] mono text-xs uppercase tracking-widest">Close_Terminal [X]</button>
                
                <div className="mb-8 border-b border-[#f59e0b]/20 pb-4">
                    <h2 className="text-3xl font-black text-white uppercase tracking-tighter">Commander Legacy</h2>
                    <p className="text-[10px] mono text-[#f59e0b]/60 uppercase tracking-[0.3em]">Neural_Sync: Verified | Medals: {unlockedIds.length}/{ACHIEVEMENTS.length}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {ACHIEVEMENTS.map(ach => {
                        const isUnlocked = unlockedIds.includes(ach.id);
                        return (
                            <div key={ach.id} className={`p-4 rounded-2xl border transition-all flex items-center gap-4 ${isUnlocked ? 'bg-[#f59e0b]/10 border-[#f59e0b]/40 shadow-[0_0_15px_rgba(245,158,11,0.05)]' : 'bg-black/20 border-white/5 opacity-40'}`}>
                                <div className={`w-12 h-12 shrink-0 rounded-full flex items-center justify-center text-xl border-2 ${isUnlocked ? 'border-[#f59e0b] bg-[#f59e0b]/20 animate-pulse' : 'border-white/10 bg-white/5'}`}>
                                    {isUnlocked ? '✦' : '◇'}
                                </div>
                                <div>
                                    <h3 className={`text-xs font-black uppercase tracking-tight ${isUnlocked ? 'text-[#f59e0b]' : 'text-white/40'}`}>{ach.name}</h3>
                                    <p className="text-[9px] mono text-[#a8a29e] mt-1 leading-tight">{isUnlocked ? ach.description : '??_ENCRYPTED_DATA_??'}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// --- DAY TRANSITION BANNER ---
const DayBanner = ({ day }) => (
    <div className="fixed inset-0 z-[70] flex items-center justify-center pointer-events-none">
        <div className="text-center animate-day-flash">
            <div className="text-[11px] mono font-bold uppercase tracking-[0.5em] text-white/30 mb-2">Timeline Continues</div>
            <div className="text-[96px] font-black text-[#f59e0b] tracking-tighter leading-none"
                style={{ textShadow: '0 0 80px rgba(245,158,11,0.6), 0 0 20px rgba(245,158,11,0.4)' }}>
                {day}
            </div>
            <div className="text-[11px] mono font-bold uppercase tracking-[0.4em] text-white/40 mt-2">Day</div>
        </div>
    </div>
);

// --- MAIN GAME VIEW ---
export default function GameView({ gameState, dispatch }) {
    const [showMedals, setShowMedals] = useState(false);
    const [shakeKey, setShakeKey] = useState(0);
    const [dayFlash, setDayFlash] = useState(null);
    const prevEventRef = useRef(null);
    const prevDayRef = useRef(gameState.day);

    // Screen shake on hostile contact
    useEffect(() => {
        const evtId = gameState.pendingEvent?.id;
        if (evtId === 'hostile_contact' && evtId !== prevEventRef.current) {
            setShakeKey(k => k + 1);
        }
        prevEventRef.current = evtId;
    }, [gameState.pendingEvent?.id]);

    // Day transition banner
    useEffect(() => {
        if (gameState.day !== prevDayRef.current && gameState.day > 1) {
            setDayFlash(gameState.day);
            const t = setTimeout(() => setDayFlash(null), 2200);
            prevDayRef.current = gameState.day;
            return () => clearTimeout(t);
        }
        prevDayRef.current = gameState.day;
    }, [gameState.day]);

    const handleAction = (label, reward, hours = 6) => {
        dispatch({ type: 'PERFORM_ACTION', payload: { actionType: label, reward, hours } });
    };

    // Detect family traits for special actions
    const familyTraits = gameState.family.flatMap(m => m.traits);
    const hasTrait = (t) => familyTraits.includes(t);

    const defenseLevel = gameState.resources.defense;
    const shieldLabel = defenseLevel >= 6 ? 'HARDENED' : defenseLevel >= 3 ? 'FORTIFIED' : defenseLevel >= 1 ? 'BASIC' : 'NONE';
    const shieldColor = defenseLevel >= 6 ? 'text-emerald-400' : defenseLevel >= 3 ? 'text-sky-400' : defenseLevel >= 1 ? 'text-amber-400' : 'text-rose-500';

    const dayTier = gameState.day < 15 ? 'STABLE' : gameState.day < 30 ? 'ELEVATED' : gameState.day < 45 ? 'CRITICAL' : 'EXTREME';
    const dayTierColor = gameState.day < 15 ? 'text-emerald-400' : gameState.day < 30 ? 'text-amber-400' : 'text-rose-400 animate-pulse';

    return (
        <div className="h-full w-full grid grid-cols-1 lg:grid-cols-12 gap-3 text-[#f59e0b] overflow-hidden">

            {/* OVERLAYS */}
            {showMedals && <AchievementModal achievements={gameState.legacy.achievements} onClose={() => setShowMedals(false)} />}
            {gameState.pendingEvent && <EventOverlay event={gameState.pendingEvent} gameState={gameState} dispatch={dispatch} />}
            {dayFlash && <DayBanner day={dayFlash} />}

            {/* ── LEFT: UNIT / CHARACTER PANEL ── */}
            <div className="lg:col-span-3 flex flex-col gap-3 h-48 lg:h-full overflow-y-auto order-2 lg:order-1">

                {/* Roster */}
                <div className="glass rounded-xl border border-white/5 p-4 bg-[#292524]/50 flex-shrink-0">
                    <div className="text-[9px] mono font-black text-[#f59e0b]/40 uppercase tracking-[0.3em] mb-3 flex justify-between items-center">
                        <span>Survivor Roster</span>
                        <button onClick={() => setShowMedals(true)} className="text-[#f59e0b]/60 hover:text-[#f59e0b] transition-colors text-[8px] border border-[#f59e0b]/20 px-2 py-0.5 rounded">MEDALS</button>
                    </div>
                    <div className="flex flex-col gap-3">
                        {gameState.family.map((p, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <div className="text-2xl shrink-0 drop-shadow-[0_0_8px_rgba(245,158,11,0.4)]">
                                    {p.ageCategory === 'INFANT' ? '👶' : 
                                     p.ageCategory === 'CHILD' ? (p.gender === 'F' ? '👧' : '👦') : 
                                     p.ageCategory === 'TEEN' ? (p.gender === 'F' ? '👩‍🦰' : '🧑‍🦱') :
                                     p.ageCategory === 'ELDER' ? (p.gender === 'F' ? '👵' : '👴') :
                                     (p.gender === 'F' ? '👩' : '👨')}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-baseline justify-between gap-1">
                                        <div className="flex items-center gap-1 min-w-0">
                                            <span className="text-[11px] font-black text-white uppercase tracking-tight leading-none truncate">{p.name.split(' ')[0]}</span>
                                            {p.role && <span className="text-[7px] bg-sky-500/20 text-sky-400 border border-sky-500/30 px-1 rounded font-black">{p.role}</span>}
                                        </div>
                                        <span className="text-[8px] mono text-[#f59e0b]/40 shrink-0">{p.age ? `Age ${p.age}` : ''}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {p.traits.length > 0 ? p.traits.map(t => (
                                            <span key={t} className="text-[7px] mono bg-[#f59e0b]/10 border border-[#f59e0b]/25 px-1.5 py-0.5 rounded text-[#f59e0b]/80 uppercase tracking-wide">{t}</span>
                                        )) : <span className="text-[7px] mono text-slate-600 italic">no specialization</span>}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {gameState.family.length === 0 && <div className="text-xs italic text-rose-500 animate-pulse mono text-center py-2">ALL MEMBERS LOST</div>}
                    </div>
                </div>

                {/* Unit Morale — character stat, not inventory */}
                <div className="glass rounded-xl border border-white/5 px-4 py-3 bg-[#292524]/50 flex-shrink-0">
                    <div className="text-[9px] mono font-black text-[#f59e0b]/40 uppercase tracking-[0.3em] mb-2">Unit Morale</div>
                    <div className="flex items-center gap-3 mb-1">
                        <div className="flex-1 h-2.5 bg-black/40 rounded-full overflow-hidden border border-white/5">
                            <div className="h-full bg-gradient-to-r from-emerald-700 to-emerald-400 transition-all duration-700"
                                style={{ width: `${Math.min(100, gameState.resources.morale)}%` }} />
                        </div>
                        <span className={`text-[14px] mono font-black w-8 text-right ${gameState.resources.morale < 20 ? 'text-rose-400 animate-pulse' : gameState.resources.morale < 40 ? 'text-amber-400' : 'text-emerald-400'}`}>
                            {Math.floor(gameState.resources.morale)}
                        </span>
                    </div>
                    <div className="text-[8px] mono text-slate-600">
                        {gameState.resources.morale >= 70 ? '🟢 Strong — unit is committed' :
                         gameState.resources.morale >= 40 ? '🟡 Moderate — tensions manageable' :
                         gameState.resources.morale >= 20 ? '🟠 Low — discipline is fraying' :
                                                            '🔴 Critical — collapse imminent'}
                    </div>
                </div>

                {/* Casualties */}
                {gameState.deceased?.length > 0 && (
                    <div className="glass rounded-xl border border-rose-500/20 px-4 py-3 bg-rose-500/5 flex-shrink-0">
                        <div className="text-[9px] mono font-black text-rose-500/40 uppercase tracking-[0.3em] mb-2">Lost</div>
                        <div className="flex flex-col gap-2">
                            {gameState.deceased.map((p, i) => (
                                <div key={i} className="flex items-center gap-2" title={`Day ${p.day}: ${p.cause}`}>
                                    <span className="text-sm">💀</span>
                                    <div>
                                        <div className="text-[9px] font-bold text-rose-400 uppercase leading-none line-through">{p.name.split(' ')[0]}</div>
                                        <div className="text-[7px] mono text-rose-500/50">{p.cause} · Day {p.day}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Commander's Log */}
                <div className="flex-1 min-h-0 overflow-hidden">
                    <ActionLog logs={gameState.logs} />
                </div>
            </div>

            {/* ── CENTER: SCENE + MAP + INVENTORY ── */}
            <div className="lg:col-span-6 flex flex-col gap-3 min-h-0 overflow-y-auto order-1 lg:order-2">

                {/* Oregon Trail Scene — hero element */}
                <div key={shakeKey}
                    className={`relative flex-shrink-0 rounded-2xl overflow-hidden border border-[#f59e0b]/15 ${shakeKey > 0 ? 'shake' : ''}`}
                    style={{ height: '280px' }}>
                    <SceneCanvas gameState={gameState} />
                    {gameState.pendingEvent?.id === 'hostile_contact' && (
                        <div key={`flash-${shakeKey}`} className="absolute inset-0 pointer-events-none flash-damage rounded-2xl" />
                    )}
                    <div className="absolute bottom-0 left-0 right-0 px-4 py-3 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
                        <div className="flex items-end justify-between">
                            <div>
                                <h2 className="text-[22px] font-black text-white uppercase tracking-tighter leading-none">{gameState.compound?.name || 'UNNAMED SITE'}</h2>
                                <div className="flex items-center gap-3 mt-0.5">
                                    <span className="text-[9px] mono text-[#f59e0b]/60">PERIMETER: <span className={shieldColor}>{shieldLabel}</span></span>
                                    {defenseLevel > 0 && <span className="text-[8px] mono text-white/25">LV {defenseLevel}</span>}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className={`text-[11px] mono font-black ${dayTierColor}`}>⚠ THREAT: {dayTier}</div>
                                <div className="text-[9px] mono text-white/35">{String(gameState.hours || 0).padStart(2,'0')}:00 · Day {gameState.day}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Survival Tutorial */}
                {!gameState.legacy.hasSeenSurvivalTutorial && (
                    <div className="flex-shrink-0 p-3 border border-rose-500/40 rounded-xl bg-rose-500/5 flex items-start gap-3">
                        <div className="text-xl shrink-0">☣️</div>
                        <div className="flex-1">
                            <p className="text-[10px] mono leading-relaxed text-rose-200">
                                <span className="font-black text-rose-400 uppercase">Situation Report: </span>
                                Every action burns supplies. Food, Water, Fuel deplete daily. Zero means someone dies. Track your Medical Supplies closely. Every decision matters.
                            </p>
                            <button onClick={() => dispatch({ type: 'DISMISS_SURVIVAL_TUTORIAL' })}
                                className="mt-2 px-3 py-1 rounded bg-rose-500 text-black text-[9px] font-black uppercase hover:bg-white transition-all">
                                UNDERSTOOD [X]
                            </button>
                        </div>
                    </div>
                )}

                {/* Sector Map — always visible */}
                <div className="flex-shrink-0 glass rounded-xl border border-white/5 p-3 bg-[#292524]/50">
                    <div className="mb-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[9px] mono font-black text-[#f59e0b]/40 uppercase tracking-[0.3em]">Tactical Surveillance Map</span>
                            {gameState.map.selectedTile ? (
                                <span className="text-[9px] mono text-[#f59e0b]/60">
                                    [{gameState.map.selectedTile.x},{gameState.map.selectedTile.y}] — {gameState.map.selectedTile.scanned ? gameState.map.selectedTile.type : 'UNSCANNED'}
                                </span>
                            ) : (
                                <span className="text-[8px] mono text-white/20 italic">Select a sector to deploy operations</span>
                            )}
                        </div>
                        <p className="text-[7px] mono text-white/30 uppercase mt-1 leading-tight">
                            The grid represents the surrounding 12km² area. Scanned sectors reveal hostiles, resources, or wildlife for potential extraction.
                        </p>
                    </div>
                    
                    <TacticalMap grid={gameState.map.grid} selectedTile={gameState.map.selectedTile} position={gameState.map.position} onSelect={(tile) => dispatch({ type: 'SELECT_TILE', payload: tile })} />
                    <MapLegend />

                    <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-[#44403c]/50">
                        <ActionButton 
                            label="Scan Sector"
                            sub={gameState.map.selectedTile ? `2h · Intel [${gameState.map.selectedTile.x},${gameState.map.selectedTile.y}]` : '2h · [LOCK] CLICK GRID TILE'}
                            disabled={!gameState.map.selectedTile || gameState.map.selectedTile.scanned}
                            onClick={() => { if (gameState.map.selectedTile) { dispatch({ type: 'SCAN_TILE', payload: { x: gameState.map.selectedTile.x, y: gameState.map.selectedTile.y } }); handleAction('Deep Scan', { morale: -1 }, 2); } }} 
                        />
                        <ActionButton 
                            label="Loot / Hunt"
                            sub={gameState.map.selectedTile?.scanned ? '6h · Deploy extraction team' : '6h · [LOCK] SCAN TILE FIRST'}
                            disabled={!gameState.map.selectedTile || !gameState.map.selectedTile.scanned || gameState.map.selectedTile.type === 'EMPTY' || gameState.map.selectedTile.type === 'PLAYER' || gameState.map.selectedTile.looted}
                            onClick={() => { 
                                const t = gameState.map.selectedTile; 
                                let r = { food: 6, water: 4 }; 
                                if (t.type === 'RESOURCE') r = { food: 28, water: 20, meds: 3 }; 
                                if (t.type === 'HOSTILE') r = { ammo: 16, meds: 5, morale: -12 }; 
                                if (t.type === 'WILDLIFE') r = { food: 34, morale: 8, ammo: -2 };
                                dispatch({ type: 'LOOT_TILE', payload: { lx: t.x, ly: t.y } }); 
                                handleAction(`Loot [${t.x},${t.y}]`, r, 6); 
                            }} 
                        />
                    </div>
                </div>

                {/* ── SUPPLIES INVENTORY (Doomsday Preppers style) ── */}
                <div className="flex-shrink-0 glass rounded-xl border border-[#f59e0b]/15 p-4 bg-[#1c1917]/80">
                    <div className="text-[9px] mono font-black text-[#f59e0b]/40 uppercase tracking-[0.3em] mb-4 pb-2 border-b border-[#44403c] flex items-center gap-2">
                        <span>📦</span><span>Supplies Inventory</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        <ResourceBar label="🥫 Food Stores" val={gameState.resources.food} max={200} color="from-amber-800 to-amber-500" warning={15} />
                        <ResourceBar label="💧 Water Supply" val={gameState.resources.water} max={200} color="from-sky-800 to-sky-500" warning={15} />
                        <ResourceBar label="⛽ Fuel Reserves" val={gameState.resources.fuel} max={100} color="from-orange-800 to-orange-500" warning={8} />
                        <ResourceBar label="🔫 Ammunition" val={gameState.resources.ammo} max={100} color="from-yellow-800 to-yellow-500" warning={8} />
                        <ResourceBar label="💊 Medical Supplies" val={gameState.resources.meds} max={20} color="from-teal-800 to-teal-400" warning={3} />
                        <div className="flex flex-col justify-center gap-1">
                            <div className="text-[8px] mono text-slate-500 uppercase">Perimeter Defense</div>
                            <div className="flex items-center gap-2">
                                <span className={`text-[20px] font-black leading-none ${shieldColor}`}>{defenseLevel}</span>
                                <div>
                                    <div className={`text-[10px] mono font-bold ${shieldColor}`}>{shieldLabel}</div>
                                    <div className="text-[8px] mono text-slate-600">
                                        {defenseLevel >= 6 ? 'Reinforced barriers' : defenseLevel >= 3 ? 'Partial fortifications' : defenseLevel >= 1 ? 'Basic perimeter' : 'Undefended'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── RIGHT: SURVIVAL DIRECTIVES ── */}
            <div className="lg:col-span-3 h-64 lg:h-full flex flex-col order-3">
                <div className="glass rounded-2xl border border-white/5 p-4 flex flex-col bg-[#292524]/30 h-full overflow-hidden">
                    <h3 className="text-[10px] font-black text-[#f59e0b] mb-3 tracking-[0.3em] uppercase border-b border-[#44403c] pb-2 flex-shrink-0">
                        Survival Directives
                    </h3>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-0">

                        <div className="text-[8px] mono text-[#f59e0b]/30 uppercase tracking-widest">Compound Ops</div>
                        <ActionButton label="Fortify Perimeter" sub={`8h · +Defense · Costs fuel · ${shieldLabel}`} onClick={() => handleAction('Fortify', { defense: 1, morale: -3, fuel: -4 }, 8)} />
                        <ActionButton label="Fuel Run" sub="4h · Find diesel, gas, propane" onClick={() => handleAction('Fuel Run', { fuel: 8, morale: -5 }, 4)} />
                        <ActionButton label="Water Collection" sub="3h · Gather, filter, store water" onClick={() => handleAction('Water Collection', { water: 14, morale: -3 }, 3)} />
                        <ActionButton label="Food Forage" sub="4h · Gather edibles from local area" onClick={() => handleAction('Forage', { food: 12, morale: -4 }, 4)} />
                        <ActionButton label="Armed Patrol" sub={`3h · 3 ammo · +Defense · ${Math.floor(gameState.resources.ammo)} rds`} disabled={gameState.resources.ammo < 3} onClick={() => handleAction('Armed Patrol', { ammo: -3, defense: 1, morale: 5 }, 3)} />
                        <ActionButton label="Scavenge Ruins" sub={hasTrait('hunter') ? '6h · Hunter: +40% yield' : '6h · Risky — varied yield'} onClick={() => handleAction('Scavenge', { food: 8, water: 5, morale: -7 }, 6)} />
                        <ActionButton label="Barter Ammo" sub="4h · Trade 10 ammo → food + meds" disabled={gameState.resources.ammo < 10} onClick={() => handleAction('Barter Ammo', { ammo: -10, food: 20, water: 12, meds: 2 }, 4)} />
                        <ActionButton label="Trade Fuel" sub="4h · Trade fuel → food + water + meds" disabled={gameState.resources.fuel < 10} onClick={() => handleAction('Trade Fuel', { fuel: -12, food: 10, water: 8, meds: 1 }, 4)} />
                        
                        <div className="pt-2 border-t border-[#44403c]/50">
                            <div className="text-[8px] mono text-emerald-400/50 uppercase tracking-widest mb-1.5">Morale & Wellness</div>
                            <ActionButton label="Strategy Session" sub="3h · Plan, coordinate · +10 Morale" onClick={() => handleAction('Family Strategy Session', { morale: 10, fuel: -1 }, 3)} />
                            <ActionButton label="Family Feast" sub="5h · Use 15 food · +25 Morale" disabled={gameState.resources.food < 15} onClick={() => handleAction('Family Feast', { food: -15, morale: 25 }, 5)} />
                            <ActionButton label="Power Rec Room" sub="4h · Use 8 fuel · +18 Morale" disabled={gameState.resources.fuel < 8} onClick={() => handleAction('Rec Room', { fuel: -8, morale: 18 }, 4)} />
                        </div>

                        {(hasTrait('hunter') || hasTrait('medic') || hasTrait('mechanic') || hasTrait('leader') || hasTrait('engineer') || hasTrait('charismatic') || hasTrait('strong')) && (
                            <div className="pt-2 border-t border-[#44403c]/50">
                                <div className="text-[8px] mono text-sky-400/50 uppercase tracking-widest mb-1.5">Unit Skills</div>
                                {hasTrait('hunter') && <ActionButton label="Hunt for Game" sub="6h · Hunter: High food · -3 ammo" highlight disabled={gameState.resources.ammo < 3} onClick={() => handleAction('Hunt', { food: 22, ammo: -3, morale: 5 }, 6)} />}
                                {hasTrait('medic') && <ActionButton label="Field Medicine" sub="3h · Medic: Treat injuries + craft meds" highlight onClick={() => handleAction('Field Medicine', { morale: 8, meds: 2 }, 3)} />}
                                {hasTrait('mechanic') && <ActionButton label="Fuel Recovery" sub="5h · Mechanic: Reclaim fuel from ruins" highlight onClick={() => handleAction('Fuel Recovery', { fuel: 12, morale: -3 }, 5)} />}
                                {hasTrait('mechanic') && <ActionButton label="Craft Ammo" sub="5h · Mechanic: Fabricate rounds from scrap" highlight onClick={() => handleAction('Craft Ammo', { ammo: 12, fuel: -4, morale: 4 }, 5)} />}
                                {hasTrait('leader') && <ActionButton label="Rally the Unit" sub="2h · Leader: Morale surge · -Food" highlight disabled={gameState.resources.food < 8} onClick={() => handleAction('Rally', { morale: 22, food: -8 }, 2)} />}
                                {hasTrait('engineer') && <ActionButton label="Compound Upgrade" sub="8h · Engineer: +2 Defense · -Fuel" highlight disabled={gameState.resources.fuel < 8} onClick={() => handleAction('Upgrade', { defense: 2, fuel: -8 }, 8)} />}
                                {hasTrait('strong') && <ActionButton label="Heavy Salvage" sub="8h · Strong: Double scavenge yield" highlight onClick={() => handleAction('Heavy Salvage', { food: 18, water: 12, morale: -5 }, 8)} />}
                                {hasTrait('charismatic') && <ActionButton label="Diplomatic Outreach" sub="4h · Charismatic: Trade network + morale" highlight onClick={() => handleAction('Diplomatic Contact', { morale: 16, food: 6, water: 4 }, 4)} />}
                            </div>
                        )}
                    </div>

                    {/* Rest Until Dawn */}
                    <div className="mt-3 pt-3 border-t border-[#44403c] flex-shrink-0">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-[8px] mono text-[#a8a29e]/50 italic">
                                {gameState.pendingEvent ? '⚠ Resolve alert first' : 'Actions burn 2–8h of daylight'}
                            </span>
                            <span className="text-[10px] mono font-bold text-[#f59e0b]/50">{String(gameState.hours || 0).padStart(2,'0')}:00</span>
                        </div>
                        <button
                            onClick={() => { const h = 24 - (gameState.hours || 0); handleAction('Rest', { morale: 8 }, h || 24); }}
                            disabled={!!gameState.pendingEvent}
                            className={`w-full py-3 rounded-xl font-black text-sm uppercase tracking-tight transition-all ${gameState.pendingEvent ? 'bg-[#44403c] text-[#a8a29e] cursor-not-allowed' : 'bg-[#f59e0b] text-black hover:bg-white shadow-[0_0_20px_rgba(245,158,11,0.15)]'}`}>
                            {gameState.pendingEvent ? '⚠ ALERT PENDING' : `Rest Until Dawn  (${24 - (gameState.hours || 0)}h)`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
